import { defineStore } from 'pinia'
import { PlayerStore } from '@/front/stores/player_store.js'
import { PlaybackStore } from '@/front/stores/playback_store.js'
import { VideoStore } from '@/front/stores/video_store.js'
import { AnnStore } from '@/front/stores/ann_store.js'
import { doAction } from '@/front/composables/ClickComposer.js'
import { focus } from '@/front/composables/UxComposer.js'
import { LeadCaptureStore } from '@/front/stores/lead_capture_store.js'
import { MessageComposer } from '@/front/composables/MessageComposer'
import { transformContent } from '@/front/composables/StringComposer'

export const ClickStore = defineStore('click', {
  state: () => {
    return {
      activeArticle: null,
      activeClipAnn: null,
      activeCollect: null,
      activeDrawing: null,
      activeImageUrl: null,
      activeLink: null,
      activeMessage: null,
      activePauseAmounts: {
        current: 0,
        original: 0,
        remaining: 0,
        progress: 0,
      },
      activeTimedPause: null,
      afkActive: false,
      audioActive: false,
      captureLeadActive: false, // true just for debugging
      clipActive: false,
      collectPrompt: null,
      endScreenActive: false,
      participateActive: false,
      genieActive: false,
      guideActive: false,
      interactionClipActive: false,
      isDrawing: false,
      magicMenuActive: false,
      payDialog: false,
      pollActive: false,
      questionActive: null,
      questionCorrectness: {
        action: '',
        answerValue: '',
        correctValue: '',
        force: false,
        isCorrect: false,
        set: false,
        value: '',
        visible: false,
      },
      resumeDialog: false,
      activeSpeechController: null
    }
  },

  actions: {
    showAfk() {
      const videoStore = VideoStore()
      if (!videoStore.video.use_afk) {
        return
      }
      this.afkActive = true
      const playerStore = PlayerStore()
      playerStore.pause("clickStore showAfk")
    },
    hideAfk() {
      this.afkActive = false
      const playerStore = PlayerStore()
      playerStore.play("clickStore hideAfk")
    },
    resetQuestionCorrectness() {
      this.questionCorrectness = {
        visible: false,
        set: false,
        isCorrect: false,
        correctValue: '',
        answerValue: '',
        action: '',
        value: '',
        force: false,
      }
    },
    startParticipate() {
      log.info(4,"clickStore startParticipate")
      this.participateActive = true
      const playerStore = PlayerStore()
      playerStore.pause("clickStore startParticipate")
    },
    stopParticipate() {
      this.participateActive = false
      const playerStore = PlayerStore()
      playerStore.play("clickStore stopParticipate")
    },
    startInteractionClip(ann) {
      log.info(4,"clickStorestartInteractionClip!", ann)
      this.interactionClipActive = true
      this.activeClipAnn = ann
    },
    finishInteractionClip() {
      this.interactionClipActive = false
      this.activeClipAnn = null
    },
    startClip() {
      log.info(4,"clickStore startClip!")
      this.clipActive = true
    },
    stopClip() {
      log.info(4,"clickStore stopClip!")
      this.finishInteractionClip()
      this.clipActive = false
    },
    startAudio() {
      this.audioActive = true
    },
    stopAudio() {
      this.audioActive = false
    },
    startCollect(ann) {
      // log.info(4,"clickStore startCollect", ann)
      this.activeCollect = ann
      this.collectPrompt = ann.click_value
    },
    stopCollect() {
      // log.info(4,"clickStore stopCollect", this.activeCollect)
      this.activeCollect = false
    },
    showMagicMenu() {
      const annStore = AnnStore()
      if (!annStore.menuAnns.length) {
        log.info(1,"🌜 clickStore showMagicMenu, no menuAnns")
        return;
      }
      this.magicMenuActive = true
    },
    hideMagicMenu() {
      this.magicMenuActive = false
    },
    showGuide() {
      log.info(4,"clickStore showGuide")
      this.guideActive = true
      log.info(4,"clickStore guideActive", this.guideActive)
    },
    hideGuide() {
      log.info(4,"clickStore hideGuide")
      this.guideActive = false
    },
    setActiveMessage(message) {
      this.activeMessage = message
    },
    clearActiveMessage() {
      this.activeMessage = null
    },
    setActiveArticle(article) {
      this.activeArticle = article
    },
    clearActiveArticle() {
      this.activeArticle = null
    },
    setActiveImageUrl(url) {
      this.activeImageUrl = url
    },
    clearActiveImageUrl() {
      this.activeImageUrl = null
    },
    startLink(url) {
      this.activeLink = url
    },
    stopLink() {
      this.activeLink = null
    },
    startGenie() {
      this.genieActive = true
    },
    stopGenie() {
      this.genieActive = false
    },
    startChatGPT() {
      this.chatGPTActive = true
    },
    stopChatGPT() {
      this.chatGPTActive = false
    },
    // For showing drawings during playback
    showDrawing(ann, useTimer = true) {
      const playerStore = PlayerStore()
      playerStore.pause("clickStore showDrawing ann timer " + ann.timer)
      this.activeDrawing = null
      this.activeDrawing = ann
      if (useTimer) {
        // this.startTimedPause(ann.timer)
        if (ann.timer == -1) {
          // until click, no timer
          log.info(4,"clickStore showDrawing until click, no timer")
        } else {
          // timer
          log.info(4,"clickStore showDrawing timer", ann.timer)
          this.startTimedPause(ann.timer)
        }
      }
    },
    hideDrawing() {
      this.activeDrawing = null
      this.clearTimedPause()
    },
    // For creating drawings
    startDrawing() {
      this.isDrawing = true
    },
    stopDrawing() {
      this.isDrawing = null
    },

    resetAll() {
      // log.info(4,"clickStore resetAll!")
      this.clearActiveMessage()
      this.clearActiveArticle()
      this.clearActiveImageUrl()
      this.stopChatGPT()
      this.stopGenie()
      this.stopCollect()
      this.stopLink()
      this.stopClip()
      this.stopAudio()
      this.clearTimedPause()
      this.finishQuestions()
      this.stopDrawing()
      this.hideDrawing()
      this.resetQuestionCorrectness()
      this.endScreenActive = false
      this.cancelSpeech('resetAll')
    },

    askGenie(video, question, time=0) {
      const playbackStore = PlaybackStore()
      const viewer = playbackStore.viewer
      log.info(4,"clickStore askGenie", video, question, viewer, time)
      return this.Api.post(`/genie/${video.id}/ask`, { prompt: question, viewer_id: viewer.id, time: time })
    },

    askChatGPT(video, question, variable = null) {
      log.info(4,"clickStore askChatGPT", video, question)
      return this.Api.post(`/genie/${video.id}/chat`, { prompt: question }).then((response) => {
        log.info(4,"clickStore askChatGPT response", response)
        if (variable) {
          const playbackStore = PlaybackStore()
          log.info(4,"clickStore askChatGPT setting variable", variable, response.data.response)
          playbackStore.setVariable(variable, response.data.response)
        }
        return response.data.response
      })
    },
    startLeadCapture() {
      const leadCaptureStore = LeadCaptureStore()
      log.info(4,"clickStore startLeadCapture, fields:", leadCaptureStore.fields)
      if (leadCaptureStore.fields.length == 0) {
        log.info(4,"clickStore startLeadCapture, no fields")
        return
      }
      this.captureLeadActive = true
      const playerStore = PlayerStore()
      playerStore.pause("clickStore startLeadCapture")
      if (playerStore.started) {
        const playbackStore = PlaybackStore()
        playbackStore.beginTracking()
      }
    },
    stopLeadCapture() {
      log.info(4,"clickStore stopLeadCapture")
      this.captureLeadActive = false
      const playbackStore = PlaybackStore()
      playbackStore.finishLeadCapture()
      const playerStore = PlayerStore()
      playerStore.play("clickStore stopLeadCapture")
    },

    showResumeDialog() {
      log.info(4,"clickStore showResumeDialog")
      this.resumeDialog = true
      const playerStore = PlayerStore()
      playerStore.takeScreenshot()
    },
    hideResumeDialog() {
      log.info(4,"clickStore hideResumeDialog")
      this.resumeDialog = false
    },

    showQuestion(ann) {
      const playerStore = PlayerStore()
      playerStore.pause("clickStore showQuestion")
      const annStore = AnnStore()
      this.resetQuestionCorrectness()
      this.questionActive = ann
      this.questionActive.showing_hint = false
      setTimeout(() => {
      if (document.activeElement?.tagName == 'BUTTON') {
          log.info(4,"💟 clickStore showQuestion blur button")
          document.activeElement.blur();
        }
        focus('question-input-' + ann.id, 100)
        if (annStore.isEditing) {
          return
        }
        if (ann.question_use_time_limit && ann.timer > 0) {
          this.startTimedPause(ann.timer)
        }
        if (ann.question_read_prompt && !annStore.isEditing) {
          this.readQuestion(ann)
        }
      }, 50)
    },
    finishQuestions() {
      const annStore = AnnStore()
      annStore.clearQuestionQueue()
      this.questionActive = null
      this.finishTimedPause()
      this.cancelSpeech('finishQuestions')
    },
    resetQuestionCorrectness() {
      this.questionCorrectness = {
        visible: false,
        set: false,
        isCorrect: false,
        correctValue: '',
        answerValue: '',
        action: '',
        value: '',
        force: false,
      }
    },

    handleVideoEnd(video) {
      log.info(4,"clickStore handleVideoEnd", video)
      const { emitMessage } = MessageComposer()
      emitMessage('progress_95')
      emitMessage('progress_100')

      // Back is button that goes back, back_auto is automatic switching
      const staticScreenActions = ['custom', 'link', 'jump', 'email', 'phone', 'results', 'download', 'video', 'back']
      const shouldShowEndScreen = staticScreenActions.includes(video.end_action)
      if (shouldShowEndScreen) {
        this.endScreenActive = true
      } else if (video.manage) {
        log.info(4,"Skipping end action for managed video")
        const annStore = AnnStore()
        annStore.showOwnerMessage("skipped_end_action_as_owner")
        
      } else {
        this.performEndAction(video)
      }
    },

    performEndAction(video) {
      const playbackStore = PlaybackStore()
      const playerStore = PlayerStore()
      const action = {
        click_action: video.end_action,
        click_value: transformContent(video.end_value, playbackStore.variables),
        click_time: video.end_argument,
      }
      log.info(1,"clickStore performEndAction", action, video, video.end_value, action.click_value)
      if (action.click_action == 'none') {
        log.info(4,"clickStore performEndAction none, skipping")
        playerStore.pause("clickStore performEndAction none")
        return
      }

      doAction(action)
      this.endScreenActive = false
    },

    async startTimedPause(seconds) {
      log.info(4,"clickStore startTimedPause", seconds)
      this.clearTimedPause()
      return new Promise((resolve) => {
        this.activePauseAmounts.original = seconds * 1000
        this.activePauseAmounts.current = 0
        this.activePauseAmounts.progress = 0
        this.activePauseAmounts.remaining = seconds * 1000
        let intervalAmount = 10
        this.activeTimedPause = setInterval(() => {
          // log.info(4,"💟  ClickStore activePause", this.activePauseAmounts.current, this.activePauseAmounts.progress)
          this.activePauseAmounts.current += intervalAmount
          this.activePauseAmounts.progress = 100 * (this.activePauseAmounts.current / this.activePauseAmounts.original)
          this.activePauseAmounts.remaining = this.activePauseAmounts.original - this.activePauseAmounts.current
          // log.info(4,"💟  ClickStore activePauseAmounts.progress", this.activePauseAmounts.progress)
          if (this.activePauseAmounts.current >= this.activePauseAmounts.original) {
            log.info(4,"💟  ClickStore activePause finished!")
            this.finishTimedPause()
          }
        }, intervalAmount)
      })
    },
    clearTimedPause() {
      if (!this.activeTimedPause) return
      clearInterval(this.activeTimedPause)
      this.activeTimedPause = null
      this.activePauseAmounts = {
        original: 0,
        current: 0,
        progress: 0,
      }
    },
    finishTimedPause() {
      if (!this.activeTimedPause) return
      const playerStore = PlayerStore()
      const wasPausing = !!this.activeTimedPause
      log.info(4,"💟  ClickStore finishTimedPause, wasPausing", wasPausing)
      if (this.questionActive) {
        const annStore = AnnStore()
        annStore.tryFinishQuestion()
      } else if (this.activeDrawing) {
        this.hideDrawing()
      }
      this.clearTimedPause()
      if (wasPausing) {
        playerStore.play("clickStore finishTimedPause")
      }
    },

    readQuestion(ann) {
      log.info(4,"clickStore readQuestion", ann)
      // Cancel any existing speech
      this.cancelSpeech('readQuestion')

      // Don't do unless we're started
      const playerStore = PlayerStore()
      if (!playerStore.mediaPlayer) {
        log.info(4,"💟 clickStore readQuestion, skipping because no media player", playerStore.decitime);
        return;
      }
      if (!playerStore.started) {
        log.info(4,"clickStore readQuestion return not started")
        return
      }
      
      let readAnswers = ann.question_read_answers
      let arr = [{ text: ann.question_prompt, delay: readAnswers ? 1000 : 0 }]
      if (readAnswers) {
        ann.question_answers.forEach(answer => {
          arr.push({ text: answer.text, delay: 500 })
        })
      }
      log.info(4,"clickStore readQuestion arr", arr)

      // Store the speech controller
      this.activeSpeechController = this.speakWithDelays(arr)
    },
    
    cancelSpeech(from='none') {
      // log.info(4,"clickStore cancelSpeech", from)
      if (this.activeSpeechController) {
        this.activeSpeechController.cancel()
        this.activeSpeechController = null
      }
      // Also cancel any ongoing speech synthesis
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel()
      }
      // log.info(4,"clickStore cancelSpeech done", from)
    },

    speakWithDelays(textArray) {
      // Speech synthesis interface
      const synth = window.speechSynthesis;

      // Flag to track if speech has been cancelled
      let isCancelled = false;
      log.info(4,"clickStore speakWithDelays isCancelled", isCancelled)

      // Current position in the array
      let currentIndex = 0;

      /**
       * Speaks the next item in the array
       */
      function speakNext() {
        log.info(4,"clickStore speakWithDelays speakNext", isCancelled, currentIndex, textArray.length)
        // Check if cancelled or end of array
        if (isCancelled || currentIndex >= textArray.length) {
          return;
        }

        const item = textArray[currentIndex];
        log.info(4,"clickStore speakWithDelays speakNext", item.text)

        // Create utterance
        const utterance = new SpeechSynthesisUtterance(item.text);

        // Handle utterance end event
        utterance.onend = () => {
          // If there's a delay and more items remain, wait before continuing
          if (item.delay && currentIndex < textArray.length - 1) {
            setTimeout(() => {
              log.info(4,"clickStore speakWithDelays speakNext setTimeout", isCancelled, currentIndex, textArray.length)
              if (!isCancelled) {
                currentIndex++;
                speakNext();
              }
            }, item.delay);
          } else if (currentIndex < textArray.length - 1) {
            // No delay specified, move to next item
            log.info(4,"clickStore speakWithDelays speakNext no delay", isCancelled, currentIndex, textArray.length)
            currentIndex++;
            speakNext();
          }
        };

        // Start speaking
        synth.speak(utterance);
      }

      /**
       * Cancels the speech and any pending delays
       */
      function cancel() {
        isCancelled = true;
        synth.cancel();
      }

      // Start the speech sequence
      log.info(4,"clickStore speakWithDelays start")
      speakNext();

      // Return controller object with cancel method
      return {
        cancel: cancel
      };
    }

  },

  getters: {
    seekbarShouldBeHidden: (state) => {
      return state.activeTimedPause
        || state.hasActiveDialog
        || state.interactionClipActive
    },
    isForceCorrect: (state) => {
      return state.questionActive && state.questionActive.question_repeat_until_correct
    },
    hasActiveDialog: (state) => {
      const videoStore = VideoStore()
      if (state.activeMessage) {
        log.info(4,"🧤 activeMessage")
        return true
      } else if (state.participateActive) {
        log.info(4,"🧤 participateActive")
        return true
      } else if (state.afkActive) {
        log.info(4,"🧤 afkActive")
        return true
      } else if (state.activeLink) {
        log.info(4,"🧤 activeLink")
        return true
      }  else if (state.payDialog) {
        log.info(4,"🧤 payDialog")
        return true
      } else if (state.activeArticle) {
        log.info(4,"🧤 activeArticle")
        return true
      } else if (state.questionActive) {
        log.info(4,"🧤 questionActive")
        return true
      } else if (state.captureLeadActive) {
        log.info(4,"🧤 captureLeadActive")
        return true
      } else if (state.genieActive) {
        log.info(4,"🧤 genieActive")
        return true
      } else if (state.chatGPTActive) {
        log.info(4,"🧤 chatGPTActive")
        return true
      } else if (state.activeCollect) {
        log.info(4,"🧤 activeCollect")
        return true
      } 
      // else if (state.magicMenuActive) {
      //   log.info(4,"🧤 magicMenuActive")
      //   return true
      // } 
      else if (state.clipActive) {
        log.info(4,"🧤 clipActive")
        return true
      } else if (state.audioActive) {
        log.info(4,"🧤 audioActive")
        return true
      } else if (state.guideActive && videoStore.video.pause_for_guide) {
        log.info(4,"🧤 guideActive")
        return true
      } else if (state.endScreenActive) {
        log.info(4,"🧤 endScreenActive")
        return true
      } else if (state.interactionClipActive) {
        log.info(4,"🧤 interactionClipActive")
        return true
      } else if (state.activeImageUrl) {
        log.info(4,"🧤 activeImageUrl")
        return true
      } else {
        return false
      }
    }
  }


})